import { createEnterpriseLead } from '@wix/ambassador-funnel-intro-v1-enterprise-lead/http'
import { Box, Heading, RadioGroup, TextButton } from '@wix/design-system'
import { useContent, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useSearchParams } from 'react-router-dom'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { useNext } from '../../../hooks/useNext'
import { withMonitoring } from '../../../monitoring/withMonitoring'
import { usePrevious } from '../../../utils/usePrevious'
import { Button } from '../../reusable/Button'
import { LogoWithBack } from '../../reusable/LogoWithBack'
import { RichText } from '../../reusable/RichText'
import { SkipButton } from '../../reusable/SkipButton'
import { VideoBox } from '../../reusable/VideoBox/VideoBox'
import { VideoLayout } from '../../reusable/VideoLayout/VideoLayout'
import { TWelcome as Content } from './Welcome.content'
import { welcomeDataHooks as dataHooks } from './Welcome.dataHooks'
import s from './Welcome.scss'

type ElementType<T> = T extends (infer U)[] ? U : never
type Option = ElementType<Content['question']['options']>

export const Welcome: FC = () => {
  const [clickedButton, setClickedButton] = useState<'next' | 'skip'>()
  const { content } = useContent<Content>()
  const { skip, skipPosition, next: nextBtn, question } = content
  const { questionKey, content: questionContent, options: questionOptions } = question
  const precheckedOption = content.question.options.find(option => option.isChecked)
  const [searchParams] = useSearchParams()
  const flowContext = searchParams.get('flowContext')
  const prevFlowContext = usePrevious(flowContext)
  const [pageLoading] = usePageLoading()

  const initialOption = useMemo(
    () => ({
      optionKey: '',
      description: '',
      title: questionContent.title,
      src: questionContent.src,
      createEnterpriseLead: false,
      isChecked: false,
      ...precheckedOption,
    }),
    [precheckedOption, questionContent.src, questionContent.title],
  )

  const [selectedOption, setSelectedOption] = useState<Option | undefined>(initialOption)
  const [hoveredOption, setHoveredOption] = useState<Option>()

  const next = useNext()

  const pageLogger = usePageLogger()

  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)

  const handleChange = (value?: number | string) => {
    const option = questionOptions.find(option => option.optionKey === value)
    if (!option) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedOption(option)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleMouseEnter = (option: Option) => {
    setHoveredOption(option)
  }

  const handleMouseLeave = () => {
    setHoveredOption(undefined)
  }

  const handleNext = async () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('next')
    void pageLogger.answer({ answer: selectedOption?.optionKey || '' })
    if (selectedOption?.createEnterpriseLead) {
      await withMonitoring(
        () =>
          ambassadorHttpClient.request(
            createEnterpriseLead({
              enterpriseLead: {
                source: 'Enterprise intro flow v3',
                ref: 'https://www.wix.com/intro',
              },
            }),
          ),
        'Enterprise-createLead',
      )
    }
    next({
      [questionKey]: { values: [selectedOption?.optionKey ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (prevFlowContext !== flowContext && !pageLoading) {
      setClickedButton(undefined)
      setHoveredOption(undefined)
      setSelectedOption(initialOption)
    }
  }, [clickedButton, flowContext, initialOption, prevFlowContext, pageLoading])

  const mainContentNode = (
    <Box direction="vertical" verticalAlign="middle" className={s.container}>
      <Box className={s.subtitleContainer}>
        <Heading className={s.subtitle} dataHook={dataHooks.subtitle()}>
          {questionContent.subtitle}
        </Heading>
      </Box>
      <Box className={s.titleContainer}>
        <Heading className={s.title} dataHook={dataHooks.title()}>
          {questionContent.title}
        </Heading>
      </Box>
      <RadioGroup
        value={selectedOption?.optionKey}
        onChange={handleChange}
        selectionArea="always"
        selectionAreaSkin="outlined"
        className={s.radio}
      >
        {questionOptions.map(option => (
          <RadioGroup.Radio
            key={option.optionKey}
            value={option.optionKey}
            onMouseEnter={() => handleMouseEnter(option)}
            onMouseLeave={handleMouseLeave}
            dataHook={dataHooks.radio({ optionKey: option.optionKey })}
          >
            {option.title}
          </RadioGroup.Radio>
        ))}
      </RadioGroup>
      <Box marginTop="42px" gap="18px" verticalAlign="middle">
        <Button
          isLoading={clickedButton === 'next'}
          onClick={handleNext}
          dataHook={dataHooks.next()}
          label={nextBtn}
          disabled={selectedOption?.optionKey === ''}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          className={!selectedOption ? s.hidden : undefined}
        />
        {skip && skipPosition === 'inline' && (
          <TextButton skin="dark" onClick={handleSkip}>
            {skip}
          </TextButton>
        )}
      </Box>
      {content.legalDisclaimer && (
        <Box position="absolute" bottom="36px" maxWidth="446px">
          <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
            {content.legalDisclaimer}
          </RichText>
        </Box>
      )}
    </Box>
  )

  const videoNode = (
    <Box position="relative" width="100%" backgroundColor="#23327B">
      <Box className={`${s.videoWrapper} ${s.visible}`}>
        <VideoBox
          src={hoveredOption?.src ?? selectedOption?.src ?? ''}
          description={hoveredOption?.description ?? selectedOption?.description}
          light
          logos={hoveredOption?.createEnterpriseLead ?? selectedOption?.createEnterpriseLead}
        />
      </Box>
    </Box>
  )

  return (
    <Box height="100vh" overflow="hidden">
      <LogoWithBack />
      <VideoLayout mainContent={mainContentNode} video={videoNode} />
      {skip && skipPosition !== 'inline' && (
        <SkipButton
          skin="light"
          skipButton={skip}
          position={skipPosition}
          onClick={handleSkip}
          dataHook={dataHooks.skip()}
        />
      )}
    </Box>
  )
}
